import {
  ActionIcon,
  Button,
  Flex,
  LoadingOverlay,
  Progress,
  Switch,
  Table,
  Text,
  Tooltip
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import classes from './styles.module.css';
import {
  IconBrandCitymapper,
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconX
} from '@tabler/icons-react';
import React, { useEffect, useRef, useState } from 'react';
import { IUsePopUpUI, PopUpUI } from '@components/UI/PopUp';
import DeleteDialog from '@/containers/pages/commonSettings/tabs/StoragePage/components/DeleteDialog';
import TransferDialog from '@/containers/pages/commonSettings/tabs/StoragePage/components/TransferDialog';
import MigrationProgressDialog from '@/containers/pages/commonSettings/tabs/StoragePage/components/MigrationProgressDialog';
import {
  IStorageConfiguration,
  StorageType,
  UserTaskStatus
} from '@/services/api/storageConfigurationApiArea/types';
import { storageConfigurationApiArea } from '@/services/api/storageConfigurationApiArea';
import EditStorageDialog from '@/containers/pages/commonSettings/tabs/StoragePage/components/EditStorageDialog';
import ClearStorageDataDialog from '@/containers/pages/commonSettings/tabs/StoragePage/components/ClearStorageDataDialog';
import { IconEraser } from '@tabler/icons';
import { showNotification } from '@mantine/notifications';

interface Props {
  element: IStorageConfiguration;
  onUpdate?(): void;
}

const StorageActionsTrigger = (props: IUsePopUpUI & { disabled?: boolean }) => {
  const { setIsOpen, disabled } = props;

  const handleOpen = () => setIsOpen(true);

  return (
    <ActionIcon mr={0} disabled={disabled} data-cy="moreActionsBtn" onClick={handleOpen}>
      <IconDotsVertical color={disabled ? '#ccc' : '#667085'} size={18} />
    </ActionIcon>
  );
};

const StorageEditTrigger = (props: IUsePopUpUI & { disabled?: boolean }) => {
  const { setIsOpen, disabled } = props;

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  return (
    <ActionIcon mr={0} data-cy="moreActionsBtn" onClick={handleOpen} disabled={disabled}>
      <IconEdit color={disabled ? '#ccc' : '#667085'} size={18} />
    </ActionIcon>
  );
};

const TransferDataTrigger = (props: IUsePopUpUI & { disabled: boolean }) => {
  const { t } = useTranslation();
  const { setIsOpen, disabled } = props;

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  return (
    <Tooltip
      withArrow
      zIndex={1000}
      label={
        disabled
          ? t('storagePage.actions.transferDisabled')
          : t('storagePage.actions.transferDescription')
      }
    >
      <Button
        fullWidth={true}
        onClick={handleOpen}
        style={{ background: disabled ? 'transparent' : '' }}
        size="sm"
        justify="flex-start"
        color="gray"
        variant="transparent"
        leftSection={<IconBrandCitymapper />}
        disabled={disabled}
      >
        {t('storagePage.actions.transfer')}
      </Button>
    </Tooltip>
  );
};

const RemoveStorageTrigger = (props: IUsePopUpUI & { disabled: boolean; reason?: string }) => {
  const { t } = useTranslation();
  const { setIsOpen, disabled, reason } = props;

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const label = disabled
    ? reason || t('storagePage.actions.cannotDelete')
    : t('storagePage.actions.deleteDescription');

  return (
    <Tooltip withArrow zIndex={1000} label={label}>
      <Button
        fullWidth={true}
        color="rgba(224, 49, 49, 0.6)"
        style={{ background: disabled ? 'transparent' : '' }}
        onClick={handleOpen}
        justify="flex-start"
        size="sm"
        variant="transparent"
        leftSection={<IconTrash />}
        disabled={disabled}
      >
        {t('storagePage.actions.delete')}
      </Button>
    </Tooltip>
  );
};

const ClearStorageTrigger = (props: IUsePopUpUI & { disabled: boolean; reason?: string }) => {
  const { t } = useTranslation();
  const { setIsOpen, disabled, reason } = props;

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const label = disabled
    ? reason || t('storagePage.actions.cannotDelete')
    : t('storagePage.actions.clearDescription');

  return (
    <Tooltip withArrow zIndex={1000} label={label}>
      <Button
        fullWidth={true}
        onClick={handleOpen}
        style={{ background: disabled ? 'transparent' : '' }}
        justify="flex-start"
        size="sm"
        variant="transparent"
        color="gray"
        leftSection={<IconEraser />}
        disabled={disabled}
      >
        {t('storagePage.actions.clear')}
      </Button>
    </Tooltip>
  );
};

const ProgressTrigger = (props: IUsePopUpUI & { migrationProgress: number; hasError: boolean }) => {
  const { setIsOpen, migrationProgress, hasError } = props;
  const handleOpen = () => setIsOpen(true);
  return (
    <div
      onClick={handleOpen}
      className={classes.progressWrapper}
      style={{ background: hasError ? '#ffe9e9' : '' }}
    >
      <Text fw={500}>{migrationProgress}%</Text>
      <Progress value={migrationProgress || 0} color={hasError ? 'red' : undefined} />
    </div>
  );
};

const getStorageTypeLabel = (type: StorageType, t: any): string => {
  switch (type) {
    case StorageType.S3:
      return t('storagePage.storageTypes.s3');
    case StorageType.Fs:
      return t('storagePage.storageTypes.fileStorage');
    case StorageType.S3Encrypted:
      return t('storagePage.storageTypes.s3Encrypted');
    case StorageType.FsEncrypted:
      return t('storagePage.storageTypes.fileStorageEncrypted');
    default:
      return t('storagePage.storageTypes.unknown');
  }
};

export function StorageTableRowItem(props: Props) {
  const { t } = useTranslation();
  const { element, onUpdate } = props;
  const [migrationProgress, setMigrationProgress] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeMigrationId, setActiveMigrationId] = useState<number | null>(null);
  const [showMigrationProgress, setShowMigrationProgress] = useState(false);
  const [isEnabled, setIsEnabled] = useState(element.IsEnabled);
  const [migrationError, setMigrationError] = useState(false);
  const [toStorageId, setToStorageId] = useState<number | null>(null);
  const [storages, setStorages] = useState<IStorageConfiguration[]>([]);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleMigrationStarted = (migrationId: number, targetStorageId?: number) => {
    setActiveMigrationId(migrationId);
    setShowMigrationProgress(true);
    setMigrationProgress(0);
    setMigrationError(false);

    if (targetStorageId !== undefined) {
      setToStorageId(targetStorageId);
    }

    // Сохраняем информацию о миграции в localStorage
    const activeMigrations = JSON.parse(localStorage.getItem('activeMigrations') || '{}');
    activeMigrations[element.Id] = {
      taskId: migrationId,
      toStorageId: targetStorageId
    };
    localStorage.setItem('activeMigrations', JSON.stringify(activeMigrations));
  };

  const handleMigrationComplete = (showSuccessNotification = false) => {
    // Показываем нотификацию о завершении, если нужно
    if (showSuccessNotification && toStorageId) {
      const fromStorage = storages.find((s) => s.Id === element.Id);
      const toStorage = storages.find((s) => s.Id === toStorageId);

      if (fromStorage && toStorage) {
        showNotification({
          title: t('storagePage.notifications.migrationCompleted', {
            from: fromStorage.Name,
            to: toStorage.Name
          }),
          message: '',
          autoClose: 5000,
          color: 'green',
          style: { zIndex: 1003 }
        });
      } else {
        showNotification({
          title: t('storagePage.notifications.migrationCompletedSimple'),
          message: '',
          autoClose: 5000,
          color: 'green',
          style: { zIndex: 1003 }
        });
      }
    }

    setActiveMigrationId(null);
    setShowMigrationProgress(false);
    setMigrationProgress(null);
    setToStorageId(null);

    // Удаляем информацию о миграции из localStorage
    const activeMigrations = JSON.parse(localStorage.getItem('activeMigrations') || '{}');
    delete activeMigrations[element.Id];
    localStorage.setItem('activeMigrations', JSON.stringify(activeMigrations));

    if (onUpdate) {
      onUpdate();
    }
  };

  const handleProgressUpdate = (percent: number) => {
    setMigrationProgress(percent);
  };

  const handleCancelMigration = async () => {
    try {
      setIsLoading(true);
      await storageConfigurationApiArea().CancelMigration({
        id: element.Id
      });
      handleMigrationComplete();
    } catch (error) {
      // Игнорируем ошибку
    } finally {
      setIsLoading(false);
    }
  };

  // Загрузка списка хранилищ для отображения имён в нотификации
  useEffect(() => {
    const fetchStorages = async () => {
      try {
        const data = await storageConfigurationApiArea().GetStorageConfigurations();
        setStorages(data);
      } catch (error) {
        // Игнорируем ошибку
      }
    };

    void fetchStorages();
  }, []);

  // Проверка активных миграций при инициализации компонента
  useEffect(() => {
    const checkActiveMigration = async () => {
      try {
        // Всегда проверяем через API, есть ли активная миграция для этого хранилища
        try {
          const data = await storageConfigurationApiArea().GetMigrationStatus({
            id: element.Id
          });

          // Проверяем, активна ли миграция
          // Миграция активна если: Status in [Register, Start, DoneWithErrors] и Percent < 100
          const isActiveMigration =
            (data.Status === UserTaskStatus.Register ||
              data.Status === UserTaskStatus.Start ||
              data.Status === UserTaskStatus.DoneWithErrors) &&
            data.Percent < 100;

          if (isActiveMigration) {
            // Активируем показ прогресса
            setActiveMigrationId(data.TaskId);
            setShowMigrationProgress(true);
            setMigrationProgress(data.Percent);

            // Восстанавливаем toStorageId из localStorage
            const activeMigrations = JSON.parse(localStorage.getItem('activeMigrations') || '{}');
            const migrationInfo = activeMigrations[element.Id];
            if (migrationInfo && migrationInfo.toStorageId !== undefined) {
              setToStorageId(migrationInfo.toStorageId);
            }

            // Сохраняем TaskId в localStorage для отслеживания
            activeMigrations[element.Id] = {
              taskId: data.TaskId,
              toStorageId: migrationInfo?.toStorageId
            };
            localStorage.setItem('activeMigrations', JSON.stringify(activeMigrations));
          } else if (data.Status === UserTaskStatus.Done || data.Percent >= 100) {
            // Миграция завершена, удаляем из localStorage
            const activeMigrations = JSON.parse(localStorage.getItem('activeMigrations') || '{}');
            delete activeMigrations[element.Id];
            localStorage.setItem('activeMigrations', JSON.stringify(activeMigrations));
          }
        } catch (error) {
          // Если миграция не найдена или произошла ошибка - это нормально, значит миграции нет

          // Очищаем localStorage на всякий случай
          const activeMigrations = JSON.parse(localStorage.getItem('activeMigrations') || '{}');
          delete activeMigrations[element.Id];
          localStorage.setItem('activeMigrations', JSON.stringify(activeMigrations));
        }
      } catch (error) {
        // Игнорируем ошибки проверки
      }
    };

    void checkActiveMigration();
  }, [element.Id]);

  useEffect(() => {
    const stopPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };

    if (activeMigrationId === null || !showMigrationProgress) {
      stopPolling();
      return;
    }

    const fetchMigrationStatus = async () => {
      try {
        // Передаем ID хранилища, а не ID задачи миграции
        const data = await storageConfigurationApiArea().GetMigrationStatus({
          id: element.Id
        });

        setMigrationProgress(data.Percent);

        const isCompleted = data.Status === UserTaskStatus.Done || data.Percent >= 100;
        const isCanceled = data.Status === UserTaskStatus.Canceled;
        const isError = data.Status === UserTaskStatus.Error;
        const hasErrors = data.Status === UserTaskStatus.DoneWithErrors;

        if (isError || isCanceled || hasErrors) {
          setMigrationError(true);
          stopPolling(); // ✅ остановка polling при ошибке/отмене/DoneWithErrors
        } else {
          setMigrationError(false);
        }

        if (isCompleted && !hasErrors) {
          stopPolling(); // ✅ остановка polling при успешном завершении

          setTimeout(() => {
            handleMigrationComplete(true); // Показываем нотификацию об успешном завершении
          }, 1000);
        }
      } catch (error) {
        stopPolling(); // ✅ остановка polling при ошибке запроса
        handleMigrationComplete(false);
      }
    };

    // Первый запрос сразу
    void fetchMigrationStatus();

    // Запускаем опрос каждые 3 секунды
    pollingIntervalRef.current = setInterval(fetchMigrationStatus, 3000);

    return () => {
      stopPolling();
    };
  }, [activeMigrationId, showMigrationProgress]);

  // Проверяем, можно ли удалить хранилище
  const canDelete = !element.IsDefault && !element.HaveFiles && !showMigrationProgress;

  const deleteReason = element.IsDefault
    ? t('storagePage.actions.deleteDefaultDisabled')
    : element.HaveFiles
    ? t('storagePage.actions.deleteHasFilesDisabled')
    : showMigrationProgress
    ? t('storagePage.actions.migrationInProgress')
    : undefined;

  // Проверяем, можно ли мигрировать из этого хранилища
  const canMigrate = !element.IsDefault && !showMigrationProgress;

  // Проверяем, можно ли редактировать
  const canEdit = !showMigrationProgress;

  const handleToggleEnabled = async () => {
    const newIsEnabled = !isEnabled;

    try {
      // Оптимистичное обновление UI - сразу переключаем свитч
      setIsEnabled(newIsEnabled);
      setIsLoading(true);

      // Отправляем запрос к API
      await storageConfigurationApiArea().SetStorageEnabled({
        id: element.Id,
        value: newIsEnabled
      });

      // Успешно - ничего дополнительно не делаем, UI уже обновлен
    } catch (error) {
      // Если ошибка - откатываем изменение
      setIsEnabled(!newIsEnabled);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Table.Tr style={{ position: 'relative' }}>
      <Table.Td className={classes.tableCell}>
        <Text>{element.Name}</Text>
      </Table.Td>
      <Table.Td className={classes.tableCell}>
        <Text>{getStorageTypeLabel(element.Type, t)}</Text>
      </Table.Td>
      <Table.Td className={classes.tableCell}>
        <Switch
          color="green"
          checked={isEnabled}
          onChange={handleToggleEnabled}
          disabled={element.IsDefault || showMigrationProgress}
        />
      </Table.Td>
      <Table.Td className={classes.tableCell}>
        {showMigrationProgress && activeMigrationId !== null && (
          <Flex align="center" gap={8}>
            <PopUpUI
              mobileSize="90%"
              desktopPosition="left-start"
              trigger={(props) => (
                <ProgressTrigger
                  {...props}
                  migrationProgress={migrationProgress || 0}
                  hasError={migrationError}
                />
              )}
            >
              {(progressPopup) => (
                <MigrationProgressDialog
                  {...progressPopup}
                  storageId={element.Id}
                  fromStorageId={element.Id}
                  toStorageId={toStorageId || 0}
                  onSuccess={handleMigrationComplete}
                  onCancel={handleMigrationComplete}
                  onProgressUpdate={handleProgressUpdate}
                  onRetry={(newTaskId) => handleMigrationStarted(newTaskId, toStorageId || 0)}
                />
              )}
            </PopUpUI>
            <Tooltip label={t('storagePage.actions.cancelMigration')} withArrow>
              <ActionIcon
                onClick={handleCancelMigration}
                variant="subtle"
                color="red"
                disabled={isLoading}
              >
                <IconX size={18} />
              </ActionIcon>
            </Tooltip>
          </Flex>
        )}
      </Table.Td>
      <Table.Td className={classes.tableCell}>
        <Flex justify="flex-end" align="center" gap={8}>
          <PopUpUI
            mobileSize="100%"
            desktopPosition="left-start"
            trigger={(props) => <StorageEditTrigger {...props} disabled={!canEdit} />}
          >
            {(params) => (
              <EditStorageDialog {...params} itemToEditId={element.Id} onSuccess={onUpdate} />
            )}
          </PopUpUI>
          <PopUpUI
            mobileSize="100%"
            desktopPosition="right-start"
            trigger={(props) => <StorageActionsTrigger {...props} disabled={!canEdit} />}
          >
            <>
              <PopUpUI
                mobileSize="90%"
                desktopPosition="left-start"
                trigger={(props) => <TransferDataTrigger {...props} disabled={!canMigrate} />}
              >
                {(transferPopup) => (
                  <TransferDialog
                    {...transferPopup}
                    fromStorageId={element.Id}
                    onSuccess={onUpdate}
                    onMigrationStarted={handleMigrationStarted}
                  />
                )}
              </PopUpUI>
              <PopUpUI
                mobileSize="90%"
                desktopPosition="left-start"
                trigger={(props) => (
                  <ClearStorageTrigger {...props} disabled={!canDelete} reason={deleteReason} />
                )}
              >
                {(deletePopup) => (
                  <ClearStorageDataDialog
                    {...deletePopup}
                    storageId={element.Id}
                    onSuccess={onUpdate}
                  />
                )}
              </PopUpUI>
              <PopUpUI
                mobileSize="90%"
                desktopPosition="left-start"
                trigger={(props) => (
                  <RemoveStorageTrigger {...props} disabled={!canDelete} reason={deleteReason} />
                )}
              >
                {(deletePopup) => (
                  <DeleteDialog {...deletePopup} storageId={element.Id} onSuccess={onUpdate} />
                )}
              </PopUpUI>
            </>
          </PopUpUI>
        </Flex>
        <LoadingOverlay visible={isLoading} style={{ position: 'absolute', zIndex: 20 }} />
      </Table.Td>
    </Table.Tr>
  );
}
